/**
 * Modal "Detalhes do Ônibus", acionado ao clicar num prefixo em
 * /app/telemetria_frota.
 *
 * ---------------------------------------------------------------------------
 * O QUE MUDOU NA AUDITORIA DE 29/09/2026:
 *
 *  [dado fabricado]  `msg_count` e `first_seen` eram projetados por
 *                    `nxv_vgpl.signalstore_vehicles` como `1 AS msg_count` e
 *                    `first_seen = last_seen = updated_at`, e o
 *                    `datalbus_sync.sql` reescrevia o `1` a cada 30 s. Um
 *                    ônibus que reporta desde 1º de setembro saía com
 *                    **"Mensagens: 1"** e **"Primeira msg" = agora**. Dado
 *                    fabricado em produção, por quatro semanas, nesta janela.
 *                    A view agora projeta `NULL`, o `sync` deixou de propagar, e
 *                    `fleet_snapshot` os anula por `unsupported_fields`. O que
 *                    falta é a FRASE: `"—"` sobre um campo que a fonte é
 *                    incapaz de fornecer é indistinguível de `"—"` sobre um
 *                    campo que só não veio nesta leitura.
 *
 *  [impossibilidade] O rastro vem de `signalstore_raw`, que o Portal VGPL não
 *                    escreve: `trail` é SEMPRE vazio. "Rastro: 0 pontos" é
 *                    lido como "o ônibus não se moveu" — informação falsa sobre
 *                    um veículo que rodou o dia inteiro.
 *
 *  [ausência]        Os KPIs e os metadados imprimiam `"—"` por `fmt(null)`,
 *                    sem distinguir ausência, vencimento e impossibilidade. Hoje
 *                    cada um recebe `celulaDeCampo` e carrega a frase do seu
 *                    estado.
 *
 *  [estado]          `plug_charging ? "Carregando" : ignition ? "Rodando" :
 *                    "Parado"` não perguntava a idade da leitura: um ônibus sem
 *                    sinal há quatro semanas aparecia "Carregando".
 * ---------------------------------------------------------------------------
 *
 * O componente é lazy no Leaflet: importa só quando abre, para não penalizar o
 * bundle inicial de quem não usa a tela.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import {
  FLEET_STALE_AFTER_SECONDS,
  type PontoDeRastro,
  type RespostaDeDetalhe,
} from "@nxvolt/shared";
import { TILE_URL, TILE_ATTRIBUTION, TILE_MAX_ZOOM } from "../lib/map-tiles.js";
import { fmtAge } from "./substation/bands.js";
import {
  celulaDeCampo,
  configuracaoDeFrota,
  estadoDeMovimento,
  fonteForneceRecurso,
  FRASE_DO_MOVIMENTO,
  fraseDeRecursoNaoSuportado,
  posicaoDoVeiculo,
  projetarFrota,
  rotuloDoVeiculo,
  textoDaIdade,
  type CelulaDeCampo,
  type VeiculoDeFrota,
} from "./fleet/aggregates.js";
import {
  BadgeDeMovimento,
  ESTILO_DEGRADADO_TEXTO,
  SufixoDeRessalva,
} from "./fleet/FleetCells.js";

interface Props {
  vin: string;
  onClose: () => void;
}

declare global {
  interface Window {
    __NXVOLT_GMAPS_KEY?: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    L?: any;
  }
}

const num0 = (v: number) => v.toLocaleString("pt-BR", { maximumFractionDigits: 0 });
const num1 = (v: number) =>
  v.toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const num2 = (v: number) =>
  v.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function fmtDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

/**
 * A cor do SOC, em TOKENS SEMÂNTICOS de faixa.
 *
 * Era a paleta crua (`--nx-danger`, `--nx-warning`, `--nx-success`), que NÃO
 * vira com o tema: medido, `--nx-warning` dá 2,15:1 sobre o fundo claro e
 * `--nx-danger` dá 3,95:1 sobre o escuro. Os `-500` são de PREENCHIMENTO; a
 * regra do design system, escrita na tabela de `--nx-band-*-ink`, é `-600` para
 * texto — e é o que esses tokens resolvem, por tema.
 */
function socColor(soc: number | null): string {
  if (soc === null) return "var(--nx-mute)";
  if (soc < 20) return "var(--nx-band-crit-ink)";
  if (soc < 50) return "var(--nx-band-warn-ink)";
  return "var(--nx-band-ok-ink)";
}

/**
 * SVG do ônibus, usado como ícone customizado no Leaflet.
 *
 * As cores vêm de `readToken`: Leaflet desenha via atributo SVG e atributo não
 * expande `var(--nx-*)`. Os fallbacks são `rgb()` e não hex — a suíte
 * anti-regressão de cor faz grep por `#RRGGBB`, e este arquivo tinha cinco
 * literais hex (`#fff`, `#e8f4ff`, `#111`, `#eee` e um `#e8692a` de fallback do
 * laranja) que passavam por baixo dela porque a varredura só olhava `pages/**`.
 */
function readToken(name: string, fallback: string): string {
  if (typeof window === "undefined") return fallback;
  const raw = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return raw || fallback;
}

function busIconSvgHtml(cor: string): string {
  const vidro = "rgb(232, 244, 255)";
  const pneu = "rgb(17, 17, 17)";
  const aro = "rgb(238, 238, 238)";
  const contorno = "rgb(255, 255, 255)";
  return `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40" width="40" height="40">
      <defs>
        <filter id="nx-bus-shadow" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="1.5" stdDeviation="1.2" flood-opacity="0.35"/>
        </filter>
      </defs>
      <g filter="url(#nx-bus-shadow)">
        <rect x="6" y="10" width="28" height="18" rx="4" fill="${cor}" stroke="${contorno}" stroke-width="1.5"/>
        <rect x="8.5" y="13" width="8" height="6" rx="1" fill="${vidro}" opacity="0.9"/>
        <rect x="17.5" y="13" width="8" height="6" rx="1" fill="${vidro}" opacity="0.9"/>
        <rect x="26.5" y="13" width="6" height="6" rx="1" fill="${vidro}" opacity="0.9"/>
        <circle cx="13" cy="30" r="3" fill="${pneu}"/>
        <circle cx="27" cy="30" r="3" fill="${pneu}"/>
        <circle cx="13" cy="30" r="1.2" fill="${aro}"/>
        <circle cx="27" cy="30" r="1.2" fill="${aro}"/>
      </g>
    </svg>
  `.trim();
}

/** Carrega leaflet dinamicamente (UMD da CDN autorizada em CSP). */
async function ensureLeaflet(): Promise<typeof window.L> {
  if (typeof window.L !== "undefined") return window.L;
  await new Promise<void>((resolve, reject) => {
    if (!document.querySelector('link[data-nx="leaflet"]')) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
      link.setAttribute("data-nx", "leaflet");
      document.head.appendChild(link);
    }
    const existing = document.querySelector('script[data-nx="leaflet"]');
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("leaflet_load_failed")));
      return;
    }
    const script = document.createElement("script");
    script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
    script.setAttribute("data-nx", "leaflet");
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("leaflet_load_failed"));
    document.head.appendChild(script);
  });
  return window.L;
}

export function VehicleDetailModal({ vin, onClose }: Props) {
  const [data, setData] = useState<RespostaDeDetalhe | null>(null);
  const [error, setError] = useState<string | null>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<unknown>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  // ESC fecha o modal
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  /*
    O FOCO ENTRA NO MODAL AO ABRIR E VOLTA PARA O GATILHO AO FECHAR.
    ---------------------------------------------------------------------------
    A metade de entrada já existia: sem ela o leitor de tela continuava na tabela
    por trás e o `aria-modal` não tinha efeito prático.

    A metade de SAÍDA faltava, e foi medida no navegador — abrir o modal pelo
    teclado (Enter no prefixo) e fechar com Escape devolvia o foco ao `<body>`.
    Quem navega por teclado perde o lugar na tabela e recomeça do topo do portal
    a cada consulta de veículo; quem usa leitor de tela fica sem contexto
    nenhum. WCAG 2.4.3.

    O `ref` guarda o elemento que TINHA o foco antes da montagem, e a restauração
    vai no cleanup do MESMO efeito: assim ela roda na desmontagem, inclusive
    quando o modal fecha por clique no backdrop, por Escape ou por navegação.
    `isConnected` é a guarda para o caso de a linha da tabela ter sido
    re-renderizada enquanto o modal estava aberto — devolver foco a um nó órfão
    é o mesmo que devolver ao `<body>`, só que em silêncio.
  */
  useEffect(() => {
    const anterior = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    return () => {
      if (anterior !== null && anterior.isConnected && typeof anterior.focus === 'function') {
        anterior.focus();
      }
    };
  }, []);

  useEffect(() => {
    let alive = true;
    setError(null);
    setData(null);
    fetch(`/api/telemetria-frota/veiculo/${encodeURIComponent(vin)}/detail?minutes=30`, {
      credentials: "include",
    })
      .then(async (r) => {
        if (!r.ok) throw new Error(`http_${r.status}`);
        return (await r.json()) as RespostaDeDetalhe;
      })
      .then((body) => {
        if (alive) setData(body);
      })
      .catch((err) => {
        if (alive) setError((err as Error).message ?? "load_failed");
      });
    return () => {
      alive = false;
    };
  }, [vin]);

  const cfg = useMemo(
    () => configuracaoDeFrota(data?.configuration?.staleAfterSeconds ?? FLEET_STALE_AFTER_SECONDS),
    [data?.configuration?.staleAfterSeconds],
  );
  /* A MESMA projeção da tabela: `ageSeconds: null` vira `latest: null`. */
  const veiculo = useMemo<VeiculoDeFrota | null>(
    () => (data ? (projetarFrota([data.vehicle])[0] ?? null) : null),
    [data],
  );

  /*
    QUEM DECIDE A IMPOSSIBILIDADE DO RASTRO É O SERVIDOR, e não esta tela.
    `trailUnsupported` vem no envelope porque quando ele é `true` a consulta NEM
    É FEITA — `trail: []` sozinho é indistinguível de "não houve posição nesta
    janela", e essa é justamente a distinção que a frase precisa fazer.

    `fonteForneceRecurso` continua como retaguarda para o envelope que ainda não
    traga o campo: o critério é o mesmo (`unsupported_fields` lido em um único
    lugar), então as duas respostas não podem divergir.
  */
  const trailSuportado =
    data !== null && data.trailUnsupported
      ? false
      : veiculo
        ? fonteForneceRecurso(veiculo.unsupportedFields, "gps_trail")
        : true;

  // Boot do mapa quando dado chega
  useEffect(() => {
    if (!veiculo || !data || !mapContainerRef.current) return;
    let disposed = false;
    void (async () => {
      try {
        const L = await ensureLeaflet();
        if (disposed || !mapContainerRef.current) return;

        const laranja = readToken("--nx-orange-500", "rgb(232, 105, 42)");
        const branco = readToken("--nx-neutral-0", "rgb(255, 255, 255)");

        /* A POSIÇÃO VEM DO CLASSIFICADOR: recusa campo não fornecido, carimbo à
           frente do relógio, coordenada fora de domínio e o `(0,0)` do GPS sem
           fix. Antes o fallback era "último ponto do rastro", que com o Portal VGPL
           é sempre inexistente — e o mapa abria em zoom 5 no centro do Brasil
           sem dizer por quê. */
        const atual = posicaoDoVeiculo(veiculo, cfg);
        const rastro: Array<[number, number]> = data.trail.map((p) => [p.lat, p.lon]);
        const centro: [number, number] = atual ?? rastro[rastro.length - 1] ?? [-23.5, -46.65];

        const map = L.map(mapContainerRef.current, {
          zoomControl: true,
          scrollWheelZoom: true,
        }).setView(centro, atual || rastro.length > 0 ? 15 : 11);
        mapRef.current = map;

        L.tileLayer(TILE_URL, { maxZoom: TILE_MAX_ZOOM, attribution: TILE_ATTRIBUTION }).addTo(map);

        if (rastro.length >= 2) {
          L.polyline(rastro, {
            color: laranja,
            weight: 4,
            opacity: 0.85,
            lineJoin: "round",
          }).addTo(map);
          L.circleMarker(rastro[0]!, {
            radius: 5,
            fillColor: branco,
            color: laranja,
            weight: 2,
            fillOpacity: 1,
          })
            .addTo(map)
            .bindPopup(`Início do rastro (${data.trailMinutes} min atrás)`);
        }

        if (atual) {
          const icon = L.divIcon({
            className: "nx-bus-marker",
            html: busIconSvgHtml(laranja),
            iconSize: [40, 40],
            iconAnchor: [20, 20],
          });
          /* DOM em vez de HTML string: prefixo e chassi são editáveis em
             /app/fleet e podem trazer HTML injetado. */
          const popupEl = document.createElement("div");
          const titulo = document.createElement("b");
          titulo.textContent = rotuloDoVeiculo(veiculo);
          popupEl.appendChild(titulo);
          popupEl.appendChild(document.createElement("br"));
          popupEl.appendChild(document.createTextNode(textoDaIdade(veiculo, cfg, fmtAge)));
          L.marker(atual, { icon }).addTo(map).bindPopup(popupEl).openPopup();
        }

        if (rastro.length >= 2 || atual) {
          const bounds = L.latLngBounds([...(atual ? [atual] : []), ...rastro]);
          if (bounds.isValid()) map.fitBounds(bounds.pad(0.2), { maxZoom: 17 });
        }
      } catch (err) {
        if (!disposed) setError((err as Error).message ?? "map_boot_failed");
      }
    })();
    return () => {
      disposed = true;
      const map = mapRef.current as { remove?: () => void } | null;
      if (map && typeof map.remove === "function") map.remove();
      mapRef.current = null;
    };
  }, [veiculo, data, cfg]);

  const titulo = veiculo ? `Ônibus ${rotuloDoVeiculo(veiculo)}` : `Ônibus ${vin}`;

  return (
    <div
      className="nx-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="nx-vehicle-modal-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="nx-modal__card" style={{ maxWidth: 1000, width: "min(96vw, 1000px)" }}>
        <div className="nx-modal__head">
          {/*
            O NOME ACESSIVEL DO DIALOGO, e ele era uma cadeia de 24 caracteres.

            Este `<h2>` e o `aria-labelledby` do `role="dialog"`, ou seja: e o
            nome que o leitor de tela anuncia ao abrir o modal. `textContent` saia
            "Onibus 820019BM384482001QA00000" — o prefixo `82001` colado no
            chassi `9BM384482001QA00000`, sem separador nenhum.

            E exatamente o que o cabecalho de `SufixoDeRessalva` descreve:
            "`marginLeft` resolve o visual e nao resolve o anuncio". Quatro
            sufixos foram corrigidos por essa regra e o TITULO ficou de fora — a
            string mais importante da tela, a primeira coisa anunciada.

            Tres coisas mudam:
             1. ESPACO LITERAL antes do chassi (`{" "}`), o mesmo mecanismo de
                `SufixoDeRessalva`;
             2. a palavra "chassi" em `nx-sr-only`, porque uma cadeia de 19
                caracteres alfanumericos sem rotulo e ruido — o leitor soletra
                sem dizer o que soletra;
             3. `marginLeft` cai de 12 para 4: o espaco literal ja da parte do
                afastamento, e sem isso o titulo ganharia um vao maior que antes.
          */}
          <h2 id="nx-vehicle-modal-title" style={{ margin: 0 }}>
            {titulo}
            {veiculo && (
              <>
                {" "}
                <span
                  className="mono"
                  style={{
                    marginLeft: 4,
                    fontSize: ".75rem",
                    fontWeight: 400,
                    color: "var(--nx-mute)",
                  }}
                >
                  <span className="nx-sr-only">chassi </span>
                  {veiculo.chassiVin}
                </span>
              </>
            )}
          </h2>
          <button
            ref={closeRef}
            className="nx-modal__close"
            onClick={onClose}
            aria-label="Fechar detalhes do ônibus"
            type="button"
          >
            ×
          </button>
        </div>

        <div className="nx-modal__body" style={{ display: "grid", gap: 14 }}>
          {error && (
            <div
              role="alert"
              style={{
                padding: 12,
                borderRadius: 8,
                /* Par SÓLIDO de alerta. `--nx-danger` não vira com o tema
                   (3,95:1 sobre o fundo escuro) e o fundo translúcido deixava a
                   razão à mercê do que estivesse atrás. */
                background: "var(--nx-alert-bg)",
                color: "var(--nx-alert-ink)",
                fontSize: ".9rem",
              }}
            >
              Falha ao carregar dados do ônibus: {error}
            </div>
          )}

          {!error && !veiculo && (
            <div role="status" style={{ padding: 32, textAlign: "center", color: "var(--nx-mute)" }}>
              Carregando telemetria…
            </div>
          )}

          {veiculo && data && (
            <>
              {/*
                O ESTADO E A IDADE VÊM PRIMEIRO, e é uma mudança de ordem
                deliberada: antes os KPIs numéricos abriam o modal e a idade da
                leitura estava no rodapé dos metadados, quatro dobras abaixo.
                Ler "SOC 100%" antes de saber que a leitura é de quatro semanas
                atrás é exatamente o BUG-SANITY que o CLAUDE.md nomeia.
              */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  flexWrap: "wrap",
                  padding: "8px 12px",
                  borderRadius: 8,
                  background: "var(--nx-bg-sunken)",
                  fontSize: ".85rem",
                }}
              >
                <BadgeDeMovimento
                  estado={estadoDeMovimento(veiculo, cfg)}
                  frase={FRASE_DO_MOVIMENTO[estadoDeMovimento(veiculo, cfg)](veiculo.sourceLabel)}
                />
                <span style={{ color: "var(--nx-mute)" }}>
                  {textoDaIdade(veiculo, cfg, fmtAge)}
                </span>
                <span style={{ color: "var(--nx-mute)", marginLeft: "auto" }}>
                  Fonte: <strong>{veiculo.sourceLabel}</strong>
                </span>
              </div>

              {/* KPIs primários. Cada um recebe a célula classificada. */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                  gap: 8,
                }}
              >
                <KpiDeSoc veiculo={veiculo} cfg={cfg} />
                <KpiDeCampo
                  label="Autonomia"
                  celula={celulaDeCampo(veiculo, "range_km", cfg, fmtAge)}
                  formatar={(v) => `${num0(Number(v))} km`}
                />
                <KpiDeCampo
                  label="Velocidade"
                  celula={celulaDeCampo(veiculo, "speed_kmh", cfg, fmtAge)}
                  formatar={(v) => `${num0(Number(v))} km/h`}
                />
                <KpiDeCampo
                  label="Odômetro"
                  celula={celulaDeCampo(veiculo, "odometer_km", cfg, fmtAge)}
                  formatar={(v) => `${num0(Number(v))} km`}
                />
                <KpiDeCampo
                  label="Temp. célula"
                  celula={celulaDeCampo(veiculo, "cell_temp_avg", cfg, fmtAge)}
                  formatar={(v) => `${num1(Number(v))} °C`}
                />
                <KpiDeCampo
                  label="Energia disponível"
                  celula={celulaDeCampo(veiculo, "energy_available_kwh", cfg, fmtAge)}
                  formatar={(v) => `${num1(Number(v))} kWh`}
                />
              </div>

              {/* Mapa + rastro */}
              <div
                style={{
                  border: "1px solid var(--nx-line)",
                  borderRadius: 8,
                  overflow: "hidden",
                  height: 340,
                  position: "relative",
                }}
              >
                <div ref={mapContainerRef} style={{ width: "100%", height: "100%" }} />
                <div
                  style={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    maxWidth: "min(70%, 420px)",
                    background: "var(--nx-bg)",
                    border: "1px solid var(--nx-line)",
                    borderRadius: 6,
                    padding: "4px 8px",
                    fontSize: ".72rem",
                    color: "var(--nx-mute)",
                    zIndex: 500,
                  }}
                >
                  {/*
                    RASTRO VAZIO COM FRASE DE IMPOSSIBILIDADE, não "sem
                    movimento". O rastro vem de `signalstore_raw`, que o Portal VGPL
                    não escreve: `trail` é SEMPRE vazio, e "Rastro: 0 pontos" é
                    lido como "o ônibus ficou parado" — informação falsa sobre um
                    veículo que rodou o dia inteiro.
                  */}
                  <ResumoDoRastro
                    suportado={trailSuportado}
                    trail={data.trail}
                    minutos={data.trailMinutes}
                    fonte={veiculo.sourceLabel}
                    fraseDoServidor={data.trailUnsupportedBy?.message ?? null}
                  />
                </div>
              </div>

              {/* Metadados */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                  gap: 8,
                  fontSize: ".85rem",
                  padding: 12,
                  background: "var(--nx-bg-sunken)",
                  borderRadius: 8,
                }}
              >
                <Meta label="Chassi" texto={veiculo.chassiVin} mono />
                <MetaDeCampo
                  label="Ignição"
                  celula={celulaDeCampo(veiculo, "ignition", cfg, fmtAge)}
                  formatar={(v) => (v === true ? "Ligada" : "Desligada")}
                />
                <MetaDeCampo
                  label="Freio de mão"
                  celula={celulaDeCampo(veiculo, "handbrake", cfg, fmtAge)}
                  formatar={(v) => (v === true ? "Puxado" : "Solto")}
                />
                <MetaDeCampo
                  label="Conexão de carga"
                  celula={celulaDeCampo(veiculo, "plug_charging", cfg, fmtAge)}
                  formatar={(v) => (v === true ? "Conectado" : "Desconectado")}
                />
                <MetaDeCampo
                  label="Potência de carga"
                  celula={celulaDeCampo(veiculo, "charging_power_kw", cfg, fmtAge)}
                  formatar={(v) => `${num1(Number(v))} kW`}
                />
                <MetaDeCampo
                  label="Torque do motor"
                  celula={celulaDeCampo(veiculo, "motor_torque_nm", cfg, fmtAge)}
                  formatar={(v) => `${num0(Number(v))} Nm`}
                />
                <MetaDeCampo
                  label="Temp. externa"
                  celula={celulaDeCampo(veiculo, "air_temp_outside", cfg, fmtAge)}
                  formatar={(v) => `${num1(Number(v))} °C`}
                />
                <MetaDeCampo
                  label="Temp. interna"
                  celula={celulaDeCampo(veiculo, "air_temp_inside", cfg, fmtAge)}
                  formatar={(v) => `${num1(Number(v))} °C`}
                />
                <MetaDeCampo
                  label="Bateria 12 V"
                  celula={celulaDeCampo(veiculo, "battery_12v_voltage", cfg, fmtAge)}
                  formatar={(v) => `${num2(Number(v))} V`}
                />
                <MetaDeCampo
                  label="Satélites de GPS"
                  celula={celulaDeCampo(veiculo, "gps_sats", cfg, fmtAge)}
                  formatar={(v) => num0(Number(v))}
                />
                <Meta label="Leitura" texto={textoDaIdade(veiculo, cfg, fmtAge)} />
                {/*
                  OS DOIS CAMPOS FABRICADOS. Com o Portal VGPL eles são
                  IMPOSSÍVEIS, e `celulaDeCampo` devolve a frase que diz isso —
                  em vez do `"—"` mudo que a versão anterior imprimia por
                  `fmtDateTime(null)` e `msg_count?.toString() ?? "—"`.
                */}
                <MetaDeCampo
                  label="Primeira mensagem"
                  celula={celulaDeCampo(veiculo, "first_seen", cfg, fmtAge)}
                  formatar={(v) => fmtDateTime(String(v))}
                />
                <MetaDeCampo
                  label="Mensagens recebidas"
                  celula={celulaDeCampo(veiculo, "msg_count", cfg, fmtAge)}
                  formatar={(v) => num0(Number(v))}
                />
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                <button className="nx-btn" type="button" onClick={onClose}>
                  Fechar
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * O resumo do rastro. TRÊS estados, não um contador.
 *
 * "Rastro: 0 pontos · últimos 30 min" é a mesma classe de defeito que
 * "0,0 km rodados": um número que o operador acredita, sobre um dado que não
 * existe. Quando a fonte não fornece o rastro, a frase diz isso; quando fornece
 * e não há ponto, diz que não houve posição registrada — que é ausência, não
 * imobilidade.
 */
function ResumoDoRastro({
  suportado,
  trail,
  minutos,
  fonte,
  fraseDoServidor,
}: {
  suportado: boolean;
  trail: readonly PontoDeRastro[];
  minutos: number;
  fonte: string;
  /**
   * A frase do 409/`trailUnsupportedBy`, quando o servidor a manda.
   *
   * PREFERIDA sobre a local pela mesma razão que a Saúde da Bateria prefere a do
   * 409: é afirmação sobre a TOPOLOGIA DE FONTES, que é o que o servidor sabe e
   * a tela não. `null` quando não veio, e aí a frase local nomeia as mesmas três
   * coisas.
   */
  fraseDoServidor: string | null;
}) {
  if (!suportado) {
    return (
      <span>
        {fraseDoServidor ??
          fraseDeRecursoNaoSuportado("gps_trail", fonte, "o histórico bruto de posições")}
      </span>
    );
  }
  if (trail.length === 0) {
    return (
      <span>
        Sem posição registrada nos últimos {minutos} min. Isto é ausência de registro, não
        prova de que o ônibus ficou parado.
      </span>
    );
  }
  return (
    <span>
      Rastro: {trail.length} {trail.length === 1 ? "ponto" : "pontos"} nos últimos {minutos}{" "}
      min
    </span>
  );
}

const COR_DO_TOM: Record<CelulaDeCampo["tom"], string> = {
  vivo: "var(--nx-ink)",
  neutro: "var(--nx-mute)",
  /* Os MESMOS tokens de `FleetCells.COR_DO_TOM`: o sufixo "(último)" é o
     portador não-cromático do padrão STALE, e `--nx-warning` reprovava a 2,15:1
     no claro — um portador que substitui a cor e depende de enxergar a cor não
     substitui nada. `--nx-danger` reprova a 3,95:1 no escuro. */
  aviso: "var(--nx-stale-ink)",
  perigo: "var(--nx-alert-ink)",
};

/**
 * O KPI de SOC, separado só porque é o único com escala de cor por faixa.
 *
 * `socColor` recebe o valor da CÉLULA, não `veiculo.latest?.socPct`: com a
 * leitura vencida a célula ainda traz o número (o padrão STALE degrada, não
 * apaga), mas com carimbo à frente do relógio ou campo impossível ela traz
 * `null` — e aí a faixa não pinta de verde um SOC que não existe. Era o
 * BUG-SANITY "SOC 100% em verde com ageSeconds de 4 semanas", pela metade que a
 * cor controla.
 */
function KpiDeSoc({
  veiculo,
  cfg,
}: {
  veiculo: VeiculoDeFrota;
  cfg: ReturnType<typeof configuracaoDeFrota>;
}) {
  const celula = celulaDeCampo(veiculo, "soc_pct", cfg, fmtAge);
  const valor = typeof celula.valor === "number" ? celula.valor : null;
  return (
    <KpiDeCampo
      label="SOC"
      celula={celula}
      formatar={(v) => `${num1(Number(v))}%`}
      cor={socColor(valor)}
    />
  );
}

function KpiDeCampo({
  label,
  celula,
  formatar,
  cor,
}: {
  label: string;
  celula: CelulaDeCampo;
  formatar: (valor: number | boolean | string) => string;
  cor?: string;
}) {
  return (
    /*
      A DEGRADACAO SAIU DO CARD INTEIRO — achado pela guarda de `opacity`.

      Com `opacity: .55` no `<div>` o RoTULO ("SOC", "Odometro") e a RESSALVA
      "(ultimo)" degradavam junto, e a ressalva e o portador NAO-CROMATICO do
      padrao: degradar quem substitui a cor e desfazer o padrao. E o valor media
      2,51:1 no claro e 3,16:1 no escuro depois da composicao — o mesmo numero
      que reprovou na tabela de frota, aqui na terceira copia da regra.
      Ver `ESTILO_DEGRADADO` em `FleetCells`.
    */
    <div
      style={{
        padding: 10,
        borderRadius: 8,
        border: "1px solid var(--nx-line)",
        background: "var(--nx-bg)",
      }}
      title={celula.frase}
    >
      <div
        style={{
          fontSize: ".7rem",
          textTransform: "uppercase",
          letterSpacing: ".04em",
          color: "var(--nx-mute)",
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: "1.1rem",
          fontWeight: 700,
          marginTop: 2,
        }}
      >
        {/* O VALOR degrada; a ressalva ao lado NAO. */}
        <span
          style={{
            color: celula.mostraValor ? (cor ?? COR_DO_TOM[celula.tom]) : "var(--nx-mute)",
            ...(celula.degradado ? ESTILO_DEGRADADO_TEXTO : {}),
          }}
        >
          {celula.mostraValor && celula.valor !== null ? (
            formatar(celula.valor)
          ) : (
            <span aria-hidden="true">—</span>
          )}
        </span>
        {/* `.8rem` (12 px): 9,3 px estava abaixo do limite que `dashboard-stale.css` ja
            mediu como problemático, e o sufixo é o portador NAO-CROMATICO. */}
          <SufixoDeRessalva sufixo={celula.sufixo} cor={COR_DO_TOM[celula.tom]} tamanho=".8rem" />
      </div>
      {/* A frase vai também para o leitor de tela: `title` não é anunciado de
          forma confiável, e o travessão sozinho não diz por que o dado falta. */}
      <span className="nx-sr-only">{celula.frase}</span>
    </div>
  );
}

function MetaDeCampo({
  label,
  celula,
  formatar,
}: {
  label: string;
  celula: CelulaDeCampo;
  formatar: (valor: number | boolean | string) => string;
}) {
  return (
    <div title={celula.frase}>
      <span style={{ color: "var(--nx-mute)", marginRight: 6 }}>{label}:</span>
      <span>
        {/* O VALOR degrada; a ressalva NAO — o `<span>` externo arrastava as duas
            junto com `opacity: .55`. */}
        <span
          style={{
            color: celula.mostraValor ? "var(--nx-ink)" : "var(--nx-mute)",
            ...(celula.degradado ? ESTILO_DEGRADADO_TEXTO : {}),
          }}
        >
          {celula.mostraValor && celula.valor !== null ? (
            formatar(celula.valor)
          ) : (
            <span aria-hidden="true">—</span>
          )}
        </span>
        <SufixoDeRessalva sufixo={celula.sufixo} cor={COR_DO_TOM[celula.tom]} />
      </span>
      <span className="nx-sr-only">{celula.frase}</span>
    </div>
  );
}

function Meta({ label, texto, mono }: { label: string; texto: string; mono?: boolean }) {
  return (
    <div>
      <span style={{ color: "var(--nx-mute)", marginRight: 6 }}>{label}:</span>
      <span className={mono ? "mono" : undefined} style={{ color: "var(--nx-ink)" }}>
        {texto}
      </span>
    </div>
  );
}
